const EXPERIMENT_ID = 'wet_trial_se_v1'
const EVENT_NAME = 'ozoneair:wet_trial_exposure'
const ARMS = ['control', 'no_trial']
const ANALYTICS_FIELDS = ['properties[_wet_trial_exposure]', 'properties[_wet_trial_first_arm]', 'properties[_wet_trial_measurement_mode]']
const RETRY_DELAYS = [250, 1000, 3000]

export function storageKey(mode = 'prepared') {
  return `${EXPERIMENT_ID}_${mode}_v2_exposure`
}
function readItem(store, key) {
  try { return store && store.getItem(key) } catch { return null }
}
function writeItem(store, key, record) {
  try { store.setItem(key, JSON.stringify(record)); return true } catch { return false }
}
export function acquireStorage(scope) {
  try { return scope && scope.localStorage } catch { return null }
}
export function enrolmentWindow(state) {
  const parseUtc = (value) => {
    if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)) return NaN
    const parsed = Date.parse(value)
    if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== (value.includes('.') ? value : value.replace('Z', '.000Z'))) return NaN
    return parsed
  }
  const start = parseUtc(state.enrolmentStart)
  const end = parseUtc(state.enrolmentEnd)
  const days = Number(state.attributionDays)
  if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || !Number.isInteger(days) || days < 1 || days > 365) return null
  return { start, end, attributionMs: days * 86400000 }
}
export function decideEnrolment(state) {
  if (!['production', 'qa'].includes(state.mode)) return { enrol: false, reason: 'prepared' }
  if (state.mode === 'qa' && state.qaEnabled !== true) return { enrol: false, reason: 'qa_disabled' }
  if (state.designMode || (state.previewMode && state.mode !== 'qa')) return { enrol: false, reason: 'editor' }
  if (state.country !== 'SE') return { enrol: false, reason: 'market' }
  if (state.privacyReady === false) return { enrol: false, reason: 'privacy' }
  if (state.analyticsAllowed !== true) return { enrol: false, reason: 'consent' }
  if (state.canPublish !== true) return { enrol: false, reason: 'analytics' }
  if (['production', 'qa'].includes(state.mode)) {
    const window = enrolmentWindow(state)
    if (!window || !Number.isFinite(state.now)) return { enrol: false, reason: 'configuration' }
    if (state.now < window.start) return { enrol: false, reason: 'not_started' }
    if (state.now >= window.end) return { enrol: false, reason: 'ended' }
  }
  return { enrol: true, reason: 'ok' }
}
function readRecord(store, mode) {
  try {
    const record = JSON.parse(readItem(store, storageKey(mode)))
    if (!record || !record.id || !ARMS.includes(record.firstArm) || !Number.isFinite(record.createdAt) || !record.publishedArms || typeof record.publishedArms !== 'object') return null
    return record
  } catch { return null }
}
export function rememberExposure(store, arm, createId, mode = 'prepared', now = Date.now()) {
  if (!store || !ARMS.includes(arm)) return null
  const existing = readRecord(store, mode)
  if (existing) return { ...existing, isNew: false }
  let id
  try { id = createId() } catch { return null }
  if (!id) return null
  const record = { id: `${mode === 'qa' ? 'qa:' : ''}${id}`, firstArm: arm, createdAt: now, publishedArms: {} }
  return writeItem(store, storageKey(mode), record) ? { ...record, isNew: true } : null
}
export function markExposurePublished(store, mode, arm) {
  const record = readRecord(store, mode)
  if (!record || !ARMS.includes(arm)) return false
  record.publishedArms[arm] = true
  return writeItem(store, storageKey(mode), record)
}
export function exposureEvent(record, arm, mode, observedAt) {
  const initial = arm === record.firstArm
  return {
    experiment_id: EXPERIMENT_ID, rendered_arm: arm, first_arm: record.firstArm, analytics_consent: true, country: 'SE',
    exposure_id: record.id, measurement_mode: mode,
    event_type: initial ? 'initial' : 'crossover',
    event_id: `${EXPERIMENT_ID}:${mode}:${record.id}:${initial ? 'initial' : `crossover:${arm}`}`,
    first_observed_at: new Date(record.createdAt).toISOString(),
    observed_at: new Date(initial ? record.createdAt : observedAt).toISOString(),
  }
}
export async function deliverExposure(publish, record, arm = record && record.firstArm) {
  if (!record) return { delivered: false, reason: 'missing' }
  if (record.publishedArms[arm]) return { delivered: false, reason: 'duplicate' }
  try { const accepted = await publish(); return accepted === false ? { delivered: false, reason: 'failed' } : { delivered: true } } catch { return { delivered: false, reason: 'failed' } }
}
export function nextRetryDelay(attempt) { return RETRY_DELAYS[attempt] ?? null }
export async function runEnrolment(input) {
  const state = { ...input.state, mode: input.mode, now: input.state.now ?? Date.now() }
  const decision = decideEnrolment(state)
  const retry = (reason, record) => {
    const delay = nextRetryDelay(input.attempt)
    if (delay == null) return { action: 'stop', reason, collected: false, record }
    input.schedule(delay)
    return { action: 'retry', reason, delay, nextAttempt: input.attempt + 1, collected: false, record }
  }
  if (!decision.enrol) {
    if (['privacy', 'analytics'].includes(decision.reason)) return retry(decision.reason)
    if (decision.reason === 'ended' && input.mode === 'production') {
      const record = readRecord(input.store, input.mode)
      const window = enrolmentWindow(state)
      if (record && record.createdAt >= window.start && record.createdAt < window.end && state.now < record.createdAt + window.attributionMs) {
        return { action: 'linked', reason: 'ended', collected: true, record }
      }
    }
    return { action: 'stop', reason: decision.reason, collected: false }
  }
  const window = enrolmentWindow(state)
  let record = readRecord(input.store, input.mode)
  if (record && window && (record.createdAt < window.start || record.createdAt >= window.end || state.now >= record.createdAt + window.attributionMs)) return { action: 'stop', reason: 'expired', collected: false }
  record = rememberExposure(input.store, input.arm, input.createId, input.mode, state.now)
  if (!record) return { action: 'stop', reason: 'storage', collected: false }
  // Publish the first observation before a later arm, retaining a stable retry identity.
  for (const arm of new Set([record.firstArm, input.arm])) {
    if (input.isCurrent && !input.isCurrent()) return { action: 'stop', reason: 'cancelled', collected: false }
    const result = await deliverExposure(() => input.publish(exposureEvent(record, arm, input.mode, state.now)), record, arm)
    if (input.isCurrent && !input.isCurrent()) return { action: 'stop', reason: 'cancelled', collected: false }
    if (result.reason === 'failed') return retry('publish', record)
    if (result.delivered) {
      if (!markExposurePublished(input.store, input.mode, arm)) return { action: 'stop', reason: 'storage', collected: false }
      record.publishedArms[arm] = true
    }
  }
  return { action: 'stored', collected: true, record }
}
export function isEligibleForm(form) {
  return !!(form && typeof form.querySelector === 'function' && form.querySelector('input[name="properties[_wet_trial_policy]"]'))
}
export function analyticsFields(record, mode = 'prepared') {
  return record ? ANALYTICS_FIELDS.map((name, index) => ({ name, value: [record.id, record.firstArm, mode][index] })) : []
}
export function stampEligibleForm(form, record, mode) {
  if (!isEligibleForm(form) || !record) return false
  analyticsFields(record, mode).forEach((field) => {
    let input = form.querySelector(`input[name="${field.name}"]`)
    if (!input) {
      input = typeof document !== 'undefined' ? document.createElement('input') : {}
      input.type = 'hidden'; input.name = field.name; form.appendChild(input)
    }
    input.value = field.value
  })
  return true
}
export function bindFormLifecycle(target, getRecord, mode) {
  const onSubmit = (event) => {
    const form = event && event.target
    return isEligibleForm(form) ? stampEligibleForm(form, getRecord(), mode) : false
  }
  target.addEventListener('submit', onSubmit, true)
  return onSubmit
}
function clearAnalyticsFields() {
  ANALYTICS_FIELDS.forEach((name) => document.querySelectorAll(`form[action*="/cart/add"] input[name="${name}"]`).forEach((input) => input.remove()))
}
function currentState(element) {
  const shopify = window.Shopify || {}
  const privacy = shopify.customerPrivacy
  const privacyReady = !!(privacy && typeof privacy.analyticsProcessingAllowed === 'function')
  return {
    privacyReady, analyticsAllowed: privacyReady && privacy.analyticsProcessingAllowed() === true,
    country: element.dataset.country || shopify.country, designMode: !!shopify.designMode, previewMode: !!shopify.previewMode,
    canPublish: !!(shopify.analytics && typeof shopify.analytics.publish === 'function'),
    mode: element.mode, qaEnabled: element.dataset.qaEnabled === 'true',
    enrolmentStart: element.dataset.enrolmentStart, enrolmentEnd: element.dataset.enrolmentEnd,
    attributionDays: element.dataset.attributionDays, now: Date.now(),
  }
}
const ElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement
export class WetTrialExperiment extends ElementBase {
  connectedCallback() {
    if (this.bound) return
    this.bound = true; this.record = null; this.retryAttempt = 0; this.generation = (this.generation || 0) + 1
    this.mode = this.dataset.measurementMode || 'prepared'
    this.onConsent = () => { this.cancelPending(); this.record = null; clearAnalyticsFields(); this.retryAttempt = 0; this.enrol() }
    document.addEventListener('visitorConsentCollected', this.onConsent)
    this.onSubmit = bindFormLifecycle(document, () => {
      if (!this.record) return null
      if (!this.recordAllowed()) { this.cancelPending(); this.record = null; clearAnalyticsFields(); return null }
      return this.record
    }, this.mode)
    this.onBuyClick = (event) => {
      const button = event.target?.closest?.('button[name="add"]')
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || !isEligibleForm(button.form)) return
      const state = currentState(this)
      if (!decideEnrolment(state).enrol) return
      const value = (name) => button.form.querySelector(`input[name="${name}"]`)?.value
      const policy = value('properties[_wet_trial_policy]')
      const match = /^(dry|wash_base|wash_steam)_(trial_30d|no_trial)_v1$/.exec(policy || '')
      const arm = value('properties[_wet_trial_arm]'), variant = value('id')
      if (!match || arm !== (match[2] === 'no_trial' ? 'no_trial' : 'control') || !/^\d+$/.test(variant || '')) return
      try {
        const result = window.Shopify.analytics.publish('ozoneair:wet_trial_buy_click', {
          experiment_id: EXPERIMENT_ID, rendered_arm: arm, product_family: match[1], variant_id: variant,
          policy_version: policy, measurement_mode: this.mode, event_id: window.crypto.randomUUID(),
        })
        Promise.resolve(result).then((accepted) => {
          if (accepted === false) console.warn('Wet-trial buy click was not dispatched')
        }, () => console.warn('Wet-trial buy click was not dispatched'))
      } catch { console.warn('Wet-trial buy click was not dispatched') }
    }
    document.addEventListener('click', this.onBuyClick, true)
    this.enrol()
  }
  recordAllowed() {
    const state = currentState(this)
    const decision = decideEnrolment(state)
    if (!this.record || (!decision.enrol && decision.reason !== 'ended')) return false
    const window = enrolmentWindow(state)
    return !!window && this.record.createdAt >= window.start && this.record.createdAt < window.end && state.now < this.record.createdAt + window.attributionMs
  }
  cancelPending() {
    this.generation += 1
    if (this.retryTimer != null) clearTimeout(this.retryTimer)
    this.retryTimer = null
  }
  disconnectedCallback() {
    this.cancelPending(); this.bound = false; this.record = null
    document.removeEventListener('visitorConsentCollected', this.onConsent)
    document.removeEventListener('submit', this.onSubmit, true)
    document.removeEventListener('click', this.onBuyClick, true)
    clearAnalyticsFields()
  }
  async enrol() {
    const generation = this.generation
    const outcome = await runEnrolment({
      state: currentState(this), store: acquireStorage(window), arm: this.dataset.renderedArm,
      mode: this.mode, attempt: this.retryAttempt,
      createId: () => window.crypto.randomUUID(),
      isCurrent: () => this.bound && generation === this.generation && currentState(this).analyticsAllowed,
      publish: (data) => window.Shopify.analytics.publish(EVENT_NAME, data),
      schedule: (delay) => { this.retryTimer = setTimeout(() => { this.retryTimer = null; this.enrol() }, delay) },
    })
    if (!this.bound || generation !== this.generation) return
    if (outcome.action === 'retry') this.retryAttempt = outcome.nextAttempt
    if (!outcome.collected) { this.record = null; clearAnalyticsFields(); return }
    this.record = outcome.record
    document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => stampEligibleForm(form, this.record, this.mode))
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('wet-trial-experiment')) customElements.define('wet-trial-experiment', WetTrialExperiment)
